import { Box } from "@chakra-ui/react"

import { useAppSelector } from "../../app/hooks"
import { SIDEWAYS_MQ } from "../../components/mediaQueries"
import { AVATAR_LAYER_ORDER, selectCharacterAvatar } from "./characterSlice"

interface CharacterAvatarPreviewProps {
  /** Rendered height of the figure, e.g. "180px". */
  height?: string
  /** Height below Chakra md (phones), when it should differ. */
  phoneHeight?: string
  /** Height on a phone held sideways, when it should differ. */
  sidewaysHeight?: string
}

/**
 * A small, non-interactive version of the character used for illustration
 * outside the game board (welcome flow, menus). Layer order matches
 * CharacterAvatar so the preview always looks like the real thing.
 */
export const CharacterAvatarPreview: React.FC<CharacterAvatarPreviewProps> = ({
  height = "180px",
  phoneHeight,
  sidewaysHeight,
}) => {
  const avatarData = useAppSelector(selectCharacterAvatar)

  // The layers fill the box, so the box alone sets the size.
  const layerStyle: React.CSSProperties = {
    height: "100%",
    objectFit: "contain",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1,
  }

  return (
    <Box
      height={phoneHeight ? { base: phoneHeight, md: height } : height}
      aspectRatio={2000 / 4803} // matches the character artwork so nothing crops
      position="relative"
      overflow="hidden"
      flexShrink={0}
      sx={
        sidewaysHeight
          ? { [SIDEWAYS_MQ]: { height: sidewaysHeight } }
          : undefined
      }
    >
      {/* 1. Skin — opaque figure fill, bottom of the stack */}
      {avatarData.skinLayer && (
        <img src={avatarData.skinLayer} alt="" style={layerStyle} />
      )}
      {/* 2. Eyes — always above skin */}
      <img src="/character/eyes-neutral.png" alt="" style={layerStyle} />
      {/* 3. Outline — line-art overlay above the skin fill and eyes,
          below clothing. Hardcoded: never user-changeable. */}
      <img src="/character/outline.png" alt="" style={layerStyle} />
      {/* 4. Remaining avatar layers (catalog order, not persisted keys) */}
      {AVATAR_LAYER_ORDER.filter(
        key => key !== "skinLayer" && avatarData[key],
      ).map(key => (
        <img key={key} src={avatarData[key]} alt="" style={layerStyle} />
      ))}
    </Box>
  )
}
